// Patient detail page — decrypted PII, clinical baseline, risk signals, SRs mini-list.

import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";
import { apiFetch } from "../lib/api.js";
import { Shell } from "../components/Shell.js";
import { PatientRiskCard } from "../components/PatientRiskCard.js";
import { SrMiniList } from "../components/SrMiniList.js";
import { AllergiesSection } from "../features/patients/AllergiesSection.js";
import { CONSENT_LABELS } from "../lib/consent-labels.js";

interface Patient {
  _id: string; full_name: string; age: number; biological_sex: string;
  city: string; primary_phone: string; emergency_contact_name?: string;
  emergency_contact_phone?: string; address?: string; primary_language?: string;
  living_situation?: string; enrolment_date?: string; plan_tier?: string;
  consent_p1_active: boolean; consent_p2_active: boolean;
  consent_p3_active: boolean; consent_p4_active: boolean;
  baseline_mobility?: string; baseline_cognitive_status?: string;
  chronic_conditions?: string[];
  primary_diagnosis?: string; gp_name?: string; gp_phone?: string;
  gp_practice?: string; gp_address?: string;
  blood_type?: string | null; contraindications?: string | null;
  assigned_coordinator_id?: string; assigned_carer_id?: string | null;
  // TASK-23 — backend resolves these from User/Carer when IDs are set; falls
  // back to ID display if undefined (e.g., legacy records or pre-N23 backend).
  coordinator_name?: string; carer_name?: string;
  // nsofar identity sync — the family-OWNER account (payer) name + the patient's relationship to them.
  account_holder_name?: string | null; patient_relationship?: string | null;
  risk_score?: number; risk_score_band?: string; risk_score_updated_at?: string;
  days_since_last_visit?: number; fall_frequency_90d?: number;
  adherence_rate_critical_meds_30d?: number; weight_change_pct_90d?: number;
  mood_decline_trend?: boolean; bp_trend_30d?: string;
  cognitive_vs_last_visit?: string;
}

const TIER_BADGE: Record<string, string> = {
  remote: "bg-slate-100 text-slate-600",
  silver: "bg-blue-100 text-blue-700",
  gold: "bg-amber-100 text-amber-700",
  gold_b2b: "bg-purple-100 text-purple-700",
};

function InfoRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex gap-2">
      <span className="w-44 text-xs text-slate-500 shrink-0">{label}</span>
      <span className="text-sm text-slate-800">{value || "—"}</span>
    </div>
  );
}

function ConsentBadge({ label, active }: { label: string; active: boolean }): JSX.Element {
  return (
    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>
      {label}: {active ? "Active" : "Revoked"}
    </span>
  );
}

function AssignmentInfo({ p }: { p: Patient }): JSX.Element | null {
  if (!p.assigned_coordinator_id && !p.assigned_carer_id) return null;
  return (
    <>
      {p.assigned_coordinator_id && (
        <InfoRow label="Assigned coordinator" value={p.coordinator_name ?? p.assigned_coordinator_id} />
      )}
      {p.assigned_carer_id && (
        <InfoRow label="Primary carer" value={p.carer_name ?? p.assigned_carer_id} />
      )}
    </>
  );
}

function PatientInfoCard({ p }: { p: Patient }): JSX.Element {
  const tier = p.plan_tier ?? "";
  const enrolDate = p.enrolment_date
    ? new Date(p.enrolment_date).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "—";
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 mb-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">{p.full_name}</h2>
          <p className="text-sm text-slate-500 mt-0.5">{p.age}y · {p.biological_sex.replace("_", " ")} · {p.city}</p>
        </div>
        {tier && (
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${TIER_BADGE[tier] ?? "bg-slate-100 text-slate-600"}`}>
            {tier.replace("_", " ")}
          </span>
        )}
      </div>
      <div className="space-y-2">
        <InfoRow label="Primary phone" value={p.primary_phone} />
        {p.account_holder_name && <InfoRow label="Account holder (payer)" value={p.account_holder_name} />}
        {p.patient_relationship && <InfoRow label="Relationship to account holder" value={p.patient_relationship} />}
        <InfoRow label="Emergency contact" value={[p.emergency_contact_name, p.emergency_contact_phone].filter(Boolean).join(" · ")} />
        <InfoRow label="Address" value={p.address ?? ""} />
        <InfoRow label="Primary language" value={p.primary_language ?? ""} />
        <InfoRow label="Living situation" value={p.living_situation ?? ""} />
        <InfoRow label="Enrolment date" value={enrolDate} />
        <AssignmentInfo p={p} />
      </div>
    </div>
  );
}

function ConsentCard({ p }: { p: Patient }): JSX.Element {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 mb-4">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Consent</p>
      <div className="flex flex-wrap gap-2">
        <ConsentBadge label={CONSENT_LABELS.P1} active={p.consent_p1_active} />
        <ConsentBadge label={CONSENT_LABELS.P2} active={p.consent_p2_active} />
        <ConsentBadge label={CONSENT_LABELS.P3} active={p.consent_p3_active} />
        <ConsentBadge label={CONSENT_LABELS.P4} active={p.consent_p4_active} />
      </div>
    </div>
  );
}

// eslint-disable-next-line complexity -- many independent conditional branches; per-branch extraction would split into co-equal helpers without semantic gain. Tracked under TASK-N19 Phase B.
function ClinicalCard({ p }: { p: Patient }): JSX.Element {
  const conditions = p.chronic_conditions?.length ? p.chronic_conditions.join(", ") : "None";
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 mb-4">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Clinical Baseline</p>
      <div className="space-y-2">
        {p.primary_diagnosis && <InfoRow label="Primary diagnosis" value={p.primary_diagnosis} />}
        <InfoRow label="Baseline mobility" value={(p.baseline_mobility ?? "").replace(/_/g, " ")} />
        <InfoRow label="Cognitive status" value={(p.baseline_cognitive_status ?? "").replace(/_/g, " ")} />
        <InfoRow label="Chronic conditions" value={conditions} />
        {p.blood_type && <InfoRow label="Blood type" value={p.blood_type} />}
        {p.contraindications && <InfoRow label="Contraindications" value={p.contraindications} />}
        {p.gp_name && <InfoRow label="GP" value={[p.gp_name, p.gp_phone].filter(Boolean).join(" · ")} />}
        {p.gp_practice && <InfoRow label="GP practice" value={p.gp_practice} />}
        {p.gp_address && <InfoRow label="GP address" value={p.gp_address} />}
      </div>
    </div>
  );
}

export function PatientDetailPage(): JSX.Element {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: patient, isLoading, isError } = useQuery<Patient>({
    queryKey: ["patient", id],
    queryFn: () => apiFetch<Patient>(`/patients/${id}`),
  });

  if (isLoading) return <Shell><p className="p-8 text-sm text-slate-400" aria-busy="true">Loading…</p></Shell>;
  if (isError || !patient) return <Shell><p className="p-8 text-sm text-red-600">Patient not found.</p></Shell>;

  return (
    <Shell>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/patients", { state: { highlightId: patient._id } })} className="text-sm text-navy-700 hover:underline">
              ← Patients
            </button>
            <h1 className="text-xl font-semibold text-slate-900">{patient.full_name}</h1>
          </div>
          <button
            onClick={() => navigate(`/patients/${id}/health-passport`)}
            className="text-sm bg-navy-700 text-white px-3 py-1.5 rounded-lg hover:bg-navy-800 transition-colors"
          >
            Health Passport
          </button>
        </div>
        <PatientInfoCard p={patient} />
        <ConsentCard p={patient} />
        <ClinicalCard p={patient} />
        <AllergiesSection patientId={id} />
        <PatientRiskCard p={patient} />
        <SrMiniList patientId={id} />
        <div className="flex flex-wrap gap-2 mt-2">
          {[
            { label: "Medications", path: "medications" },
            { label: "Monthly Check-ins", path: "monthly-checkins" },
            { label: "Teleconsults", path: "teleconsults" },
            { label: "Healthcare Events", path: "healthcare-events" },
            { label: "Health Reports", path: "health-reports" },
            { label: "Vaccinations", path: "vaccinations" },
          ].map(({ label, path }) => (
            <button key={path} onClick={() => navigate(`/patients/${id}/${path}`)}
              className="text-sm px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
              {label} →
            </button>
          ))}
        </div>
      </div>
    </Shell>
  );
}
